'use client'

import { PublicationTemplateOption } from '@/lib/publications/types'

interface Props {
  templates: PublicationTemplateOption[]
  onSelect: (template: PublicationTemplateOption) => void
}

function TemplateMiniPreview({ template }: { template: PublicationTemplateOption }) {
  const p = template.preview
  const hasThumbnail = Boolean(p.thumbnailUrl)

  return (
    <div
      className="relative aspect-square overflow-hidden rounded-3xl p-4 shadow-sm"
      style={{ background: p.background }}
    >
      {hasThumbnail ? (
        <>
          <img
            src={p.thumbnailUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/24 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/34 via-transparent to-transparent" />
        </>
      ) : null}

      {!hasThumbnail ? (
        <>
          <div
            className="absolute -right-8 top-8 h-28 w-28 rounded-full blur-2xl"
            style={{ background: p.accent, opacity: 0.28 }}
          />
          <div
            className="absolute -left-8 bottom-8 h-28 w-28 rounded-full blur-2xl"
            style={{ background: '#FFFFFF', opacity: 0.75 }}
          />

          {p.layout === 'botanical_product' ? (
            <>
              <div
                className="absolute right-5 top-16 h-20 w-10 rounded-full opacity-40"
                style={{ background: p.accent }}
              />
              <div
                className="absolute right-10 bottom-10 h-24 w-12 rounded-[28px] border"
                style={{
                  background: 'rgba(255,255,255,0.72)',
                  borderColor: 'rgba(0,0,0,0.08)',
                }}
              />
            </>
          ) : null}

          {p.layout === 'editorial_clean' ? (
            <div className="absolute right-4 top-4 bottom-4 w-[1px] bg-black/10" />
          ) : null}

          {p.layout === 'luxury_glow' ? (
            <div
              className="absolute right-8 bottom-8 h-20 w-20 rounded-full"
              style={{
                background: `radial-gradient(circle, ${p.accent}, transparent 68%)`,
                opacity: 0.35,
              }}
            />
          ) : null}
        </>
      ) : null}

      <div className="relative z-10 flex h-full flex-col justify-between">
        <div
          className="w-fit rounded-full bg-white/65 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: p.muted }}
        >
          {p.badge || 'Template'}
        </div>

        <div>
          <div
            className="whitespace-pre-line text-[28px] font-serif leading-[0.95]"
            style={{ color: p.text }}
          >
            {p.headline}
          </div>
          <div
            className="mt-2 max-w-[80%] text-[10px] leading-4"
            style={{ color: p.muted }}
          >
            {p.subheadline}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PublicationTemplateSelector({ templates, onSelect }: Props) {
  return (
    <div className="mt-3 rounded-[28px] border border-neutral-200 bg-white p-3 shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="mb-3 px-1">
        <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          Elige un diseño
        </div>
        <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          Puedes cambiarlo después desde Creator Studio.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-1 xs:grid-cols-1">
        {templates.map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template)}
            className="group rounded-[26px] border border-neutral-200 bg-neutral-50 p-2 text-left transition hover:-translate-y-0.5 hover:border-neutral-300 hover:bg-white hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700"
          >
            <TemplateMiniPreview template={template} />

            <div className="px-2 pb-1 pt-3">
              <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                {template.name}
              </div>
              <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-neutral-500 dark:text-neutral-400">
                {template.description}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}